import {Fragment, useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {notification} from "antd";
import ActionBar from "~/layout/ActionBar";
import {Icon, Button, Loading, Modal} from "~/components";
import {
	marketingProjectErrorSelector,
	marketingProjectFilterSelector,
	marketingProjectItemsSelector,
	marketingProjectLoadingSelector,
	marketingProjectPaginationSelector,
	marketingProjectActions
} from "../marketingProjectSlice";
import {
	MarketingProjectTable,
	MarketingProjectFormAddEdit,
	MarketingProjectFormBudget,
	MarketingProjectFormAssign,
	MarketingProjectFormReactivate,
	MarketingProjectSearchBar
} from "../components";
import {apiError, strToTime, handleRequest} from "~/utils";
import {useCan, useGroup} from "~/hooks";
import {marketingProjectApi} from "~/api";

function MarketingProject() {

	const can = {
		add: useCan('marketingProjectAdd'),
		edit: useCan('marketingProjectEdit'),
		status: useCan('marketingProjectStatus'),
		assign: useCan('marketingProjectAssign'),
		budget: useCan('marketingProjectBudget'),
		delete: useCan('marketingProjectDelete'),
	};

	const listGroup = useGroup();

	const dispatch = useDispatch();

	const items      = useSelector(marketingProjectItemsSelector);
	const loading    = useSelector(marketingProjectLoadingSelector);
	const error      = useSelector(marketingProjectErrorSelector);
	const pagination = useSelector(marketingProjectPaginationSelector);
	const filter     = useSelector(marketingProjectFilterSelector);

	const [itemEdit, setItemEdit] = useState({});

	const [openModal, setOpenModal] = useState({
		addEdit    : false,
		budget     : false,
		assign     : false,
		reactivate : false,
		delete     : false,
	});

	const handleModalOpen = (modal) => {
		openModal[modal] = true;
		setOpenModal({...openModal});
	}

	const handleModalClose = (modal) => {
		openModal[modal] = false;
		setOpenModal({...openModal});
	}

	useEffect(() => {
		handleReLoading();
	}, [filter]);

	if (error) {
		notification.error({message: 'Lỗi', description: error});
	}

	const handleReLoading = () => {
		dispatch(marketingProjectActions.fetchData(filter));
	}

	//Thêm / sửa
	const handleSaveItem = async (data, isEdit) => {
		let messageAction = 'Tạo';
		if (isEdit) {
			if (!itemEdit?.id) {
				notification.error({message: 'Lỗi', description: 'Không có dự án để cập nhật'});
				return;
			}
			data.id = itemEdit.id;
			messageAction = 'Cập nhật';
		}
		data.startDate = strToTime(data.startDate);
		data.endDate = strToTime(data.endDate);
		let [err, response] = (isEdit) ? await handleRequest(marketingProjectApi.update(data)) : await handleRequest(marketingProjectApi.add(data));
		let message = apiError(`${messageAction} dự án thất bại`, err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: `${messageAction} dự án thành công`});
			if (isEdit) dispatch(marketingProjectActions.update(response.data));
			else dispatch(marketingProjectActions.add(response.data));
			handleModalClose('addEdit');
		}
	}

	//Cập nhật trạng thái (inline trên bảng)
	const handleStatus = async (item, status) => {
		let [err, response] = await handleRequest(marketingProjectApi.status({id: item.id, status}));
		let message = apiError('Cập nhật trạng thái thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đã cập nhật trạng thái'});
			dispatch(marketingProjectActions.update({...item, status: response.data.status}));
		}
	}

	//Cập nhật đánh giá chạy quảng cáo (inline trên bảng)
	const handleAdStatus = async (item, adStatus) => {
		let [err, response] = await handleRequest(marketingProjectApi.update({id: item.id, adStatus}));
		let message = apiError('Cập nhật đánh giá thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đã cập nhật đánh giá'});
			dispatch(marketingProjectActions.update(response.data));
		}
	}

	//Phân kỹ thuật
	const handleAssign = async (data, item) => {
		if (!item?.id) return;
		let [err, response] = await handleRequest(marketingProjectApi.assign({id: item.id, assignId: data.assignId}));
		let message = apiError('Phân công kỹ thuật thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đã phân công kỹ thuật'});
			dispatch(marketingProjectActions.update({id: item.id, assignId: data.assignId, assign: response.data.assign}));
			handleModalClose('assign');
		}
	}

	//Kích hoạt lại dự án (gia hạn) — dùng lại dự án cũ, đưa về "đang thực hiện" + endDate mới
	const handleReactivate = async (data, item) => {
		if (!item?.id) return;
		let [err, response] = await handleRequest(marketingProjectApi.reactivate({id: item.id, endDate: strToTime(data.endDate)}));
		let message = apiError('Kích hoạt lại dự án thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đã kích hoạt lại dự án'});
			dispatch(marketingProjectActions.update(response.data));
			handleModalClose('reactivate');
		}
	}

	//Xóa
	const handleDelete = async () => {
		if (!itemEdit?.id) return;
		let [err, response] = await handleRequest(marketingProjectApi.delete(itemEdit.id));
		let message = apiError('Xóa dự án thất bại', err, response);
		if (!message) {
			notification.success({message: 'Thành công', description: 'Đã xóa dự án'});
			dispatch(marketingProjectActions.delete(itemEdit.id));
			handleModalClose('delete');
		}
	}

	const handlePaginationChange = (page) => {
		dispatch(marketingProjectActions.setFilter({...filter, page}));
	}

	const handleSearchChange = (newFilter) => {
		dispatch(marketingProjectActions.setFilterWithDebounce(newFilter));
	};

	const handleFilterChange = (key, value) => {
		dispatch(marketingProjectActions.setFilter({...filter, [key]: value, page: 1}));
	};

	return (
		<Fragment>
			<ActionBar title={'Dự án Marketing'}>
				{can.add && <Button outline leftIcon={Icon.plusCircle} onClick={() => {setItemEdit({}); handleModalOpen('addEdit')}}>Thêm dự án</Button>}
				<Button outline onClick={handleReLoading}>{Icon.reload}</Button>
			</ActionBar>
			<div className="container">
				<div className="content">
					{loading && <Loading/>}
					<MarketingProjectSearchBar filter={filter} onSearchChange={handleSearchChange} onChange={handleFilterChange} listGroup={listGroup} />
					{items && <MarketingProjectTable
						items={items}
						can={can}
						pagination={pagination}
						onPaginationChange={handlePaginationChange}
						setItemEdit={setItemEdit}
						openModal={handleModalOpen}
						onChangeStatus={handleStatus}
						onChangeAdStatus={handleAdStatus}
					/>}
				</div>
			</div>
			{
				(can.add || can.edit) &&
				<Modal title="Dự án marketing" visible={openModal.addEdit} onCancel={() => {handleModalClose('addEdit')}}>
					<MarketingProjectFormAddEdit item={itemEdit} onHandleSubmit={handleSaveItem} />
				</Modal>
			}
			{
				(can.budget && itemEdit?.id) &&
				<Modal title="Ngân sách theo ngày" visible={openModal.budget} onCancel={() => {handleModalClose('budget')}}>
					<MarketingProjectFormBudget item={itemEdit} onUpdated={handleReLoading} />
				</Modal>
			}
			{
				(can.assign && itemEdit?.id) &&
				<Modal title="Phân kỹ thuật" visible={openModal.assign} onCancel={() => {handleModalClose('assign')}}>
					<MarketingProjectFormAssign item={itemEdit} onHandleSubmit={handleAssign} />
				</Modal>
			}
			{
				(can.status && itemEdit?.id) &&
				<Modal title="Kích hoạt lại dự án" visible={openModal.reactivate} onCancel={() => {handleModalClose('reactivate')}}>
					<MarketingProjectFormReactivate item={itemEdit} onHandleSubmit={handleReactivate} />
				</Modal>
			}
			{
				(can.delete && itemEdit?.id) &&
				<Modal title="Xóa dự án" visible={openModal.delete} onCancel={() => {handleModalClose('delete')}}>
					<p>Bạn muốn xóa dự án <b>{itemEdit?.name}</b>?</p>
					<div className="d-flex justify-content-end gap modal-bottom pd-1">
						<Button white leftIcon={Icon.close} onClick={() => {handleModalClose('delete')}}> Đóng </Button>
						<Button primary leftIcon={Icon.delete} onClick={handleDelete}> Xóa </Button>
					</div>
				</Modal>
			}
		</Fragment>
	)
}

export default MarketingProject;
